import {useCallback, useEffect, useMemo, useState} from "react";
import type {FormEvent, ReactNode} from "react";
import {
  createAiModel, createAiProvider, createAiWorker, listAiModels, listAiProviders,
  listAiVersions, listAiWorkers, listApprovedTools, transitionAiObject,
  updateAiModel, updateAiProvider, updateAiWorker
} from "../../api/aiAdministration";
import type {
  AiAdministrationVersion, AiLifecycleAction, AiModel, AiModelDraft, AiObjectType,
  AiProvider, AiProviderDraft, AiWorker, AiWorkerDraft, ApprovedTool
} from "../../api/aiAdministration";
import {useAuth} from "../../auth/AuthContext";
import {PageHeader} from "../../components/PageHeader";

const ADMIN_ROLES = new Set(["workbench-operator", "chief-product-architect", "security-engineer"]);
const APPROVER_ROLES = new Set(["workbench-approver", "governance-approver", "security-engineer"]);
type AiConfig = AiProvider | AiModel | AiWorker;
type AiDraft = AiProviderDraft | AiModelDraft | AiWorkerDraft;

export function LiveWorkerRegistry() {
  const {authorizedFetch} = useAuth();
  const [workers, setWorkers] = useState<AiWorker[] | null>(null);
  const [models, setModels] = useState<AiModel[]>([]);
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      listAiWorkers(authorizedFetch, controller.signal),
      listAiModels(authorizedFetch, controller.signal),
      listAiProviders(authorizedFetch, controller.signal)
    ]).then(([nextWorkers, nextModels, nextProviders]) => {
      setWorkers(nextWorkers);
      setModels(nextModels);
      setProviders(nextProviders);
    }).catch(reason => {
      if (!controller.signal.aborted) setError(message(reason));
    });
    return () => controller.abort();
  }, [authorizedFetch]);

  const modelById = useMemo(() => new Map(models.map(model => [model.id, model])), [models]);
  const providerById = useMemo(() => new Map(providers.map(provider => [provider.id, provider])), [providers]);

  return <>
    <PageHeader eyebrow="AI Workforce / Live registry" title="Workers"
      description="Authoritative governed worker configurations. Configuration lifecycle is shown separately from runtime activity." />
    <section className="panel page-panel live-ai-page">
      <div className="configuration-boundary" role="note">
        <strong>Configuration state</strong>
        <span>Runtime activity is not inferred. Worker Runs remains deferred until the existing run contracts provide a clean operational read model.</span>
      </div>
      {error && <p className="form-message error" role="alert">{error}</p>}
      {workers === null && !error && <p role="status">Loading governed workers…</p>}
      {workers?.length === 0 && <div className="empty-state"><h2>No workers registered</h2><p>This scoped registry contains no governed worker configuration.</p></div>}
      <div className="card-grid live-worker-grid">
        {workers?.map(worker => {
          const model = modelById.get(worker.modelId);
          const provider = model ? providerById.get(model.providerId) : undefined;
          return <article className="worker-card live-worker-card" key={worker.id}>
            <span className="worker-avatar" aria-hidden="true">{worker.name.slice(0, 2).toUpperCase()}</span>
            <div><h2>{worker.name}</h2><p>{worker.key}</p></div>
            <span className={`badge ${worker.lifecycle === "ACTIVE" ? "safe" : ""}`}>{worker.lifecycle}</span>
            <dl>
              <Row label="Actor type" value="AI worker configuration" />
              <Row label="Role" value={worker.role} />
              <Row label="Provider" value={provider?.name ?? "Unavailable in scope"} />
              <Row label="Model" value={model?.name ?? "Unavailable in scope"} />
              <Row label="Allowed tools" value={worker.allowedTools.join(", ") || "None"} />
              <Row label="Prohibited tools" value={worker.prohibitedTools.join(", ") || "None recorded"} />
              <Row label="Network" value={worker.networkPolicy} />
              <Row label="Version" value={`v${worker.version} · ${formatDate(worker.updatedAt)}`} />
            </dl>
          </article>;
        })}
      </div>
    </section>
  </>;
}

export const ProviderAdministration = () =>
  <AdministrationPage kind="providers" title="AI providers" description="Governed provider endpoints and external credential references." />;
export const ModelAdministration = () =>
  <AdministrationPage kind="models" title="AI models" description="Provider-backed model configurations, limits, locality, and lifecycle." />;
export const WorkerAdministration = () =>
  <AdministrationPage kind="workers" title="AI workers" description="Governed worker instructions, scopes, approved tools, and lifecycle." />;

export function ApprovedToolAdministration() {
  const {authorizedFetch} = useAuth();
  const [tools, setTools] = useState<ApprovedTool[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    void listApprovedTools(authorizedFetch, controller.signal)
      .then(setTools)
      .catch(reason => !controller.signal.aborted && setError(message(reason)));
    return () => controller.abort();
  }, [authorizedFetch]);
  return <>
    <PageHeader eyebrow="AI Administration / Read-only catalog" title="Approved tools"
      description="Assignment-safe catalog from the existing Approved Tool Registry. This view grants no execution authority." />
    <section className="panel page-panel live-ai-page">
      {error && <p className="form-message error" role="alert">{error}</p>}
      {tools === null && !error && <p role="status">Loading approved tools…</p>}
      <div className="ai-tool-grid">{tools?.map(tool => <article key={tool.key} className="ai-tool-card">
        <h2>{tool.key}</h2><span className="badge safe">Approved</span>
        <dl><Row label="Network" value={tool.networkPolicy} /><Row label="Timeout" value={`${tool.timeoutSeconds}s`} />
          <Row label="CPU quota" value={`${tool.cpuQuotaPercent}%`} /><Row label="Memory" value={`${tool.memoryMiB} MiB`} />
          <Row label="Process limit" value={String(tool.processLimit)} /></dl>
      </article>)}</div>
    </section>
  </>;
}

function AdministrationPage({kind, title, description}: {
  kind: AiObjectType; title: string; description: string;
}) {
  const {authorizedFetch, user} = useAuth();
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [models, setModels] = useState<AiModel[]>([]);
  const [workers, setWorkers] = useState<AiWorker[]>([]);
  const [tools, setTools] = useState<ApprovedTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState<AiConfig | null>(null);
  const [versions, setVersions] = useState<AiAdministrationVersion[] | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit" | null>(null);
  const [rationale, setRationale] = useState("");
  const isAdmin = user?.roles.some(role => ADMIN_ROLES.has(role)) ?? false;
  const isApprover = user?.roles.some(role => APPROVER_ROLES.has(role)) ?? false;

  const reload = useCallback(async (signal?: AbortSignal) => {
    const [nextProviders, nextModels, nextWorkers, nextTools] = await Promise.all([
      listAiProviders(authorizedFetch, signal), listAiModels(authorizedFetch, signal),
      listAiWorkers(authorizedFetch, signal), listApprovedTools(authorizedFetch, signal)
    ]);
    setProviders(nextProviders); setModels(nextModels); setWorkers(nextWorkers); setTools(nextTools);
    const items = kind === "providers" ? nextProviders : kind === "models" ? nextModels : nextWorkers;
    setSelected(current => current ? items.find(item => item.id === current.id) ?? null : null);
  }, [authorizedFetch, kind]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void reload(controller.signal).catch(reason => {
      if (!controller.signal.aborted) setError(message(reason));
    }).finally(() => !controller.signal.aborted && setLoading(false));
    return () => controller.abort();
  }, [reload]);

  useEffect(() => {
    if (!selected) { setVersions(null); return; }
    const controller = new AbortController();
    setVersions(null);
    void listAiVersions(authorizedFetch, kind, selected.id, controller.signal)
      .then(setVersions)
      .catch(reason => !controller.signal.aborted && setError(message(reason)));
    return () => controller.abort();
  }, [authorizedFetch, kind, selected?.id, selected?.version]);

  const items: AiConfig[] = kind === "providers" ? providers : kind === "models" ? models : workers;
  const exactAuthor = Boolean(selected && versions?.some(version =>
    version.contentHash === selected.contentHash
      && ["CREATED", "UPDATED"].includes(version.eventType)
      && version.actorId === user?.subject));

  async function save(draft: AiDraft) {
    setError(""); setNotice("");
    try {
      let saved: AiConfig;
      if (kind === "providers") saved = selected && formMode === "edit"
        ? await updateAiProvider(authorizedFetch, selected as AiProvider, draft as AiProviderDraft)
        : await createAiProvider(authorizedFetch, draft as AiProviderDraft);
      else if (kind === "models") saved = selected && formMode === "edit"
        ? await updateAiModel(authorizedFetch, selected as AiModel, draft as AiModelDraft)
        : await createAiModel(authorizedFetch, draft as AiModelDraft);
      else saved = selected && formMode === "edit"
        ? await updateAiWorker(authorizedFetch, selected as AiWorker, draft as AiWorkerDraft)
        : await createAiWorker(authorizedFetch, draft as AiWorkerDraft);
      setSelected(saved); setFormMode(null); setNotice(`${saved.name} saved as ${saved.lifecycle}.`);
      await reload();
    } catch (reason) { setError(message(reason)); }
  }

  async function transition(action: AiLifecycleAction) {
    if (!selected) return;
    if (!rationale.trim()) { setError("Lifecycle rationale is required."); return; }
    setError(""); setNotice("");
    try {
      const updated = await transitionAiObject<AiConfig>(
        authorizedFetch, kind, selected.id, selected.version, action, rationale.trim());
      setSelected(updated); setRationale(""); setNotice(`${updated.name} ${action} completed.`);
      await reload();
    } catch (reason) { setError(message(reason)); }
  }

  return <>
    <PageHeader eyebrow={`AI Administration / ${kind}`} title={title} description={description}
      aside={isAdmin ? <button type="button" onClick={() => { setSelected(null); setFormMode("create"); }}>Create {singular(kind)}</button> : undefined} />
    <section className="panel page-panel live-ai-page">
      <div className="authorization-note" role="note">
        <strong>Backend-authoritative governance</strong>
        <span>Visible controls reflect token roles and lifecycle for usability; every mutation is re-authorized by the service.</span>
      </div>
      {error && <p className="form-message error" role="alert">{error}</p>}
      {notice && <p className="form-message success" role="status">{notice}</p>}
      {loading ? <p role="status">Loading governed configurations…</p> : <div className="ai-admin-layout">
        <div className="registry-table ai-admin-table" tabIndex={0} aria-label={`${title} registry`}>
          <table><thead><tr><th>Key / name</th><th>Lifecycle</th><th>Version</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{items.map(item => <tr key={item.id}><td><strong>{item.key}</strong><span>{item.name}</span></td>
              <td><span className={`badge ${item.lifecycle === "ACTIVE" ? "safe" : ""}`}>{item.lifecycle}</span></td>
              <td>v{item.version}</td><td><button type="button" className="secondary" onClick={() => { setSelected(item); setFormMode(null); }}>View details</button></td></tr>)}</tbody>
          </table>
          {items.length === 0 && <div className="empty-state"><h2>No {kind}</h2><p>No governed configurations exist in this scope.</p></div>}
        </div>
        {formMode && <ConfigForm kind={kind} current={formMode === "edit" ? selected : null}
          providers={providers} models={models} tools={tools} onCancel={() => setFormMode(null)} onSave={save} />}
        {selected && !formMode && <ConfigDetail kind={kind} item={selected} providers={providers} models={models}>
          <div className="ai-detail-actions">
            {isAdmin && selected.lifecycle === "DRAFT" && <button type="button" className="secondary" onClick={() => setFormMode("edit")}>Edit</button>}
            {(isAdmin || isApprover) && <label>Lifecycle rationale<textarea value={rationale} onChange={event => setRationale(event.target.value)} rows={2} /></label>}
            <LifecycleButtons lifecycle={selected.lifecycle} isAdmin={isAdmin} isApprover={isApprover}
              exactAuthor={exactAuthor} versionsLoaded={versions !== null} onAction={transition} />
            {isApprover && ["VALIDATED", "APPROVED"].includes(selected.lifecycle) && (versions === null || exactAuthor) &&
              <p className="separation-reason" role="note">{versions === null
                ? "Approval availability is withheld until immutable author evidence loads."
                : "Approval requires a different named human from the author of this exact version."}</p>}
          </div>
          <VersionEvidence versions={versions} />
        </ConfigDetail>}
      </div>}
    </section>
  </>;
}

function LifecycleButtons({lifecycle, isAdmin, isApprover, exactAuthor, versionsLoaded, onAction}: {
  lifecycle: string; isAdmin: boolean; isApprover: boolean; exactAuthor: boolean; versionsLoaded: boolean;
  onAction(action: AiLifecycleAction): void;
}) {
  const action = lifecycle === "DRAFT" ? "validate" : lifecycle === "VALIDATED" ? "approve"
    : lifecycle === "APPROVED" ? "activate" : lifecycle === "ACTIVE" ? "suspend" : null;
  const permitted = action === "validate" ? isAdmin : isApprover;
  const separationBlocked = (action === "approve" || action === "activate") && (!versionsLoaded || exactAuthor);
  return <div className="form-actions wrap">
    {action && permitted && <button type="button" disabled={separationBlocked} onClick={() => onAction(action)}>{label(action)}</button>}
    {isApprover && lifecycle !== "RETIRED" && <button type="button" className="danger-link" onClick={() => onAction("retire")}>Retire</button>}
  </div>;
}

function ConfigDetail({kind, item, providers, models, children}: {
  kind: AiObjectType; item: AiConfig; providers: AiProvider[]; models: AiModel[]; children: ReactNode;
}) {
  const providerById = new Map(providers.map(provider => [provider.id, provider]));
  const modelById = new Map(models.map(model => [model.id, model]));
  return <article className="ai-config-detail">
    <header><div><p className="eyebrow">Exact governed configuration</p><h2>{item.name}</h2><code>{item.contentHash}</code></div><span className="badge">{item.lifecycle}</span></header>
    <dl className="preview-meta"><Row label="Key" value={item.key} /><Row label="Version" value={`v${item.version}`} />
      <Row label="Updated by" value={item.updatedBy} /><Row label="Updated" value={formatDate(item.updatedAt)} />
      {kind === "models" && <><Row label="Provider" value={providerById.get((item as AiModel).providerId)?.name ?? "Unavailable"} />
        <Row label="Provider model" value={(item as AiModel).providerModelId} /></>}
      {kind === "workers" && <><Row label="Role" value={(item as AiWorker).role} />
        <Row label="Model" value={modelById.get((item as AiWorker).modelId)?.name ?? "Unavailable"} />
        <Row label="Allowed tools" value={(item as AiWorker).allowedTools.join(", ") || "None"} />
        <Row label="Network policy" value={(item as AiWorker).networkPolicy} /></>}
    </dl>{children}
  </article>;
}

function ConfigForm({kind, current, providers, models, tools, onCancel, onSave}: {
  kind: AiObjectType; current: AiConfig | null; providers: AiProvider[]; models: AiModel[]; tools: ApprovedTool[];
  onCancel(): void; onSave(draft: AiDraft): Promise<void>;
}) {
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      if (kind === "providers") await onSave(providerDraft(data));
      else if (kind === "models") await onSave(modelDraft(data));
      else await onSave(workerDraft(data, form));
    } catch (reason) {
      form.querySelector<HTMLElement>("[data-form-error]")!.textContent = message(reason);
    }
  }
  return <form className="authoring-form ai-config-form" onSubmit={event => void submit(event)}>
    <div className="form-heading"><div><p className="eyebrow">{current ? "Edit exact Draft" : "Create governed Draft"}</p>
      <h2>{current ? current.name : `New ${singular(kind)}`}</h2></div><button type="button" className="secondary" onClick={onCancel}>Close</button></div>
    <p className="form-message error empty-message" role="alert" data-form-error />
    {kind === "providers" && <ProviderFields current={current as AiProvider | null} />}
    {kind === "models" && <ModelFields current={current as AiModel | null} providers={providers} />}
    {kind === "workers" && <WorkerFields current={current as AiWorker | null} models={models} tools={tools} />}
    <div className="form-actions"><button type="button" className="secondary" onClick={onCancel}>Cancel</button><button type="submit">{current ? "Save Draft" : "Create Draft"}</button></div>
  </form>;
}

function ProviderFields({current}: {current: AiProvider | null}) {
  return <div className="form-grid"><Field name="key" label="Key" value={current?.key} required /><Field name="name" label="Name" value={current?.name} required />
    <Field name="adapterType" label="Adapter type" value={current?.adapterType} required /><Field name="endpoint" label="Endpoint" value={current?.endpoint} required />
    <Field name="credentialReference" label="External credential reference" value={current?.credentialReference ?? ""} span /></div>;
}

function ModelFields({current, providers}: {current: AiModel | null; providers: AiProvider[]}) {
  return <div className="form-grid"><Field name="key" label="Key" value={current?.key} required /><Field name="name" label="Name" value={current?.name} required />
    <label>Provider<select name="providerId" defaultValue={current?.providerId ?? ""} required><option value="">Select a governed provider</option>{providers.map(provider => <option key={provider.id} value={provider.id}>{provider.name} · {provider.lifecycle}</option>)}</select></label>
    <Field name="providerModelId" label="Provider model ID" value={current?.providerModelId} required />
    <Field name="capabilities" label="Capabilities" value={current?.capabilities.join(", ")} />
    <Field name="parameters" label="Parameters (JSON)" value={JSON.stringify(current?.parameters ?? {}, null, 2)} multiline />
    <Field name="contextWindow" label="Context window" value={current?.contextWindow?.toString()} type="number" />
    <Field name="maxOutputUnits" label="Max output units" value={current?.maxOutputUnits?.toString()} type="number" />
    <Field name="dataLocality" label="Data locality" value={current?.dataLocality ?? "CANADA"} required />
    <Field name="costClass" label="Cost class" value={current?.costClass ?? "STANDARD"} required /></div>;
}

function WorkerFields({current, models, tools}: {current: AiWorker | null; models: AiModel[]; tools: ApprovedTool[]}) {
  return <div className="form-grid"><Field name="key" label="Key" value={current?.key} required /><Field name="name" label="Name" value={current?.name} required />
    <Field name="role" label="Worker role" value={current?.role} required /><label>Model<select name="modelId" defaultValue={current?.modelId ?? ""} required><option value="">Select a governed model</option>{models.map(model => <option key={model.id} value={model.id}>{model.name} · {model.lifecycle}</option>)}</select></label>
    <Field name="description" label="Description" value={current?.description} multiline span required />
    <Field name="specialization" label="Specialization (JSON)" value={JSON.stringify(current?.specialization ?? {}, null, 2)} multiline />
    <Field name="systemInstruction" label="System instruction" value={current?.systemInstruction} multiline required />
    <Field name="operatingInstruction" label="Operating instruction" value={current?.operatingInstruction} multiline required />
    <Field name="productScope" label="Product scope" value={current?.productScope.join("\n")} multiline />
    <Field name="domainScope" label="Domain scope" value={current?.domainScope.join("\n")} multiline />
    <Field name="repositoryScope" label="Repository scope" value={current?.repositoryScope.join("\n")} multiline />
    <Field name="fileScope" label="File scope" value={current?.fileScope.join("\n")} multiline />
    <fieldset className="span-2 tool-selector"><legend>Allowed approved tools</legend><p>Loaded from the authoritative Approved Tool Registry.</p>
      {tools.map(tool => <label key={tool.key}><input type="checkbox" name="allowedTools" value={tool.key} defaultChecked={current?.allowedTools.includes(tool.key)} />{tool.key}<small>{tool.networkPolicy} · {tool.timeoutSeconds}s</small></label>)}</fieldset>
    <Field name="prohibitedTools" label="Prohibited tools or authority names" value={current?.prohibitedTools.join("\n")} multiline span />
    <label>Network policy<select name="networkPolicy" defaultValue={current?.networkPolicy ?? "DENY_ALL"}><option value="DENY_ALL">DENY_ALL</option></select></label>
    <label className="immutable-control"><input type="checkbox" checked readOnly />Named-human approval required</label></div>;
}

function VersionEvidence({versions}: {versions: AiAdministrationVersion[] | null}) {
  return <section className="version-history"><header><div><p className="eyebrow">Immutable evidence</p><h2>Version and lifecycle history</h2></div></header>
    {versions === null ? <p role="status">Loading immutable author evidence…</p> : <ol>{versions.map(version => <li key={version.id}>
      <strong>v{version.version} · {version.eventType}</strong><span>{version.actorId}</span><time>{formatDate(version.recordedAt)}</time></li>)}</ol>}</section>;
}

function Field({name, label: fieldLabel, value = "", required = false, multiline = false, span = false, type = "text"}: {
  name: string; label: string; value?: string; required?: boolean; multiline?: boolean; span?: boolean; type?: string;
}) {
  return <label className={span ? "span-2" : undefined}>{fieldLabel}{multiline
    ? <textarea name={name} defaultValue={value} required={required} rows={4} />
    : <input name={name} type={type} defaultValue={value} required={required} />}</label>;
}

function providerDraft(data: FormData): AiProviderDraft {
  return {key: value(data, "key"), name: value(data, "name"), adapterType: value(data, "adapterType"),
    endpoint: value(data, "endpoint"), credentialReference: value(data, "credentialReference") || null};
}
function modelDraft(data: FormData): AiModelDraft {
  return {key: value(data, "key"), name: value(data, "name"), providerId: value(data, "providerId"),
    providerModelId: value(data, "providerModelId"), capabilities: list(value(data, "capabilities")),
    parameters: jsonObject(value(data, "parameters")), contextWindow: integer(data, "contextWindow"),
    maxOutputUnits: integer(data, "maxOutputUnits"), dataLocality: value(data, "dataLocality"), costClass: value(data, "costClass")};
}
function workerDraft(data: FormData, form: HTMLFormElement): AiWorkerDraft {
  return {key: value(data, "key"), name: value(data, "name"), role: value(data, "role"), description: value(data, "description"),
    modelId: value(data, "modelId"), specialization: jsonObject(value(data, "specialization")),
    systemInstruction: value(data, "systemInstruction"), operatingInstruction: value(data, "operatingInstruction"),
    productScope: list(value(data, "productScope")), domainScope: list(value(data, "domainScope")),
    repositoryScope: list(value(data, "repositoryScope")), fileScope: list(value(data, "fileScope")),
    allowedTools: [...form.querySelectorAll<HTMLInputElement>('input[name="allowedTools"]:checked')].map(input => input.value),
    prohibitedTools: list(value(data, "prohibitedTools")), networkPolicy: "DENY_ALL", humanApprovalRequired: true};
}
function value(data: FormData, key: string) { return String(data.get(key) ?? "").trim(); }
function list(input: string) { return input.split(/[\n,]/).map(item => item.trim()).filter(Boolean); }
function integer(data: FormData, key: string) { const input = value(data, key); return input ? Number(input) : null; }
function jsonObject(input: string): Record<string, unknown> {
  if (!input.trim()) return {};
  const parsed = JSON.parse(input) as unknown;
  if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") throw new Error("JSON fields must contain an object.");
  return parsed as Record<string, unknown>;
}
function singular(kind: AiObjectType) { return kind.slice(0, -1); }
function label(action: string) { return action[0].toUpperCase() + action.slice(1); }
function message(reason: unknown) { return reason instanceof Error ? reason.message : "AI Administration operation failed."; }
function formatDate(value: string) { return new Intl.DateTimeFormat(undefined, {dateStyle: "medium", timeStyle: "short"}).format(new Date(value)); }
function Row({label: rowLabel, value: rowValue}: {label: string; value: string}) { return <div><dt>{rowLabel}</dt><dd>{rowValue}</dd></div>; }
